import { useEffect, useState } from "react";
import { Modal, Button, Alert, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPrint, faCheck } from "@fortawesome/free-solid-svg-icons";
import { AviTracerApi } from "../avitracerApi";
import Flight from "../models/flight";

type RowState = "idle" | "printing" | "printed" | "error";

interface DirectPrintModalProps {
  // When non-null the modal is shown for this flight; null hides it.
  flight: Flight | null;
  onClose: () => void;
}

// Lists every printable document for a flight and sends the chosen ones to the on-site print
// server. Unavailable docs stay visible but disabled. Printed state is kept only while the modal
// is open (it resets on close/reopen) so the operator can print several docs without it closing.
function DirectPrintModal({ flight, onClose }: DirectPrintModalProps) {
  const [docs, setDocs] = useState<AviTracerApi.PrintableDocInfo[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [rowState, setRowState] = useState<Record<string, RowState>>({});
  const [rowError, setRowError] = useState<Record<string, string>>({});
  const [confirmKey, setConfirmKey] = useState<string | null>(null);

  useEffect(() => {
    if (!flight) return;
    // Fresh, ephemeral state each time the modal opens for a flight.
    setDocs(null); setLoadError(null); setRowState({}); setRowError({}); setConfirmKey(null);
    let cancelled = false;
    (async () => {
      try {
        const res = await AviTracerApi.getPrintableDocs(flight.id!);
        if (!cancelled) setDocs(res.docs);
      } catch (e: any) {
        if (!cancelled) setLoadError(typeof e === "string" ? e : "Could not load documents.");
      }
    })();
    return () => { cancelled = true; };
  }, [flight]);

  const anyPrinting = Object.values(rowState).some((s) => s === "printing");

  const doPrint = async (key: string) => {
    if (!flight) return;
    setConfirmKey(null);
    setRowState((s) => ({ ...s, [key]: "printing" }));
    setRowError((s) => ({ ...s, [key]: "" }));
    try {
      await AviTracerApi.printFlightDoc(flight.id!, key);
      setRowState((s) => ({ ...s, [key]: "printed" }));
    } catch (e: any) {
      setRowState((s) => ({ ...s, [key]: "error" }));
      setRowError((s) => ({ ...s, [key]: typeof e === "string" ? e : "Print failed." }));
    }
  };

  const onPrintClick = (d: AviTracerApi.PrintableDocInfo) => {
    if (d.confirm && confirmKey !== d.key) { setConfirmKey(d.key); return; }
    doPrint(d.key);
  };

  return (
    <Modal show={flight !== null} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Direct Print</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loadError && <Alert variant="danger">{loadError}</Alert>}
        {!docs && !loadError && (
          <div className="text-center py-3 text-muted"><Spinner animation="border" size="sm" /> Loading documents…</div>
        )}
        {docs && docs.length === 0 && <div className="text-muted">No printable documents for this flight.</div>}
        {docs && docs.length > 0 && (
          <div className="d-grid gap-2">
            {docs.map((d) => {
              const st = rowState[d.key] ?? "idle";
              return (
                <div key={d.key} className="border rounded p-2">
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="me-2">
                      <div className="fw-medium">{d.label}</div>
                      {!d.available && <small className="text-muted d-block">Not available for this flight</small>}
                      {d.available && d.note && <small className="text-muted d-block fst-italic">{d.note}</small>}
                      {st === "printed" && <small className="text-success"><FontAwesomeIcon icon={faCheck} /> Sent to printer</small>}
                      {st === "error" && <small className="text-danger">{rowError[d.key]}</small>}
                    </div>
                    <Button
                      variant={st === "printed" ? "outline-success" : "dark"}
                      size="sm"
                      className="text-nowrap"
                      disabled={!d.available || anyPrinting}
                      onClick={() => onPrintClick(d)}
                    >
                      {st === "printing"
                        ? (<><Spinner animation="border" size="sm" /> Printing…</>)
                        : st === "printed"
                        ? "Print again"
                        : (<><FontAwesomeIcon icon={faPrint} /> Print</>)}
                    </Button>
                  </div>
                  {confirmKey === d.key && (
                    <Alert variant="warning" className="mt-2 mb-0 py-2">
                      <small>This will print all the pages, including all flight documents and the NOTAMs.</small>
                      <div className="mt-2 d-flex gap-2">
                        <Button size="sm" variant="warning" onClick={() => doPrint(d.key)}>Print all pages</Button>
                        <Button size="sm" variant="outline-secondary" onClick={() => setConfirmKey(null)}>Cancel</Button>
                      </div>
                    </Alert>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
}

export default DirectPrintModal;
